import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { DarkVenueHeatmapRequest, toUid } from './DarkVenueHeatmapRequest';
import { DarkVenueHeatmapApiRequest } from 'widgets/darkVenueHeatmap/api/DarkVenueHeatmapApiRequest';
import { DarkVenueHeatmapApiResponse } from 'widgets/darkVenueHeatmap/api/DarkVenueHeatmapApiResponse';
import { selectWidgetsDataState } from 'widgets/store/selectors.ts';
import { DarkVenueHeatmapDataState } from 'widgets/darkVenueHeatmap/store/data/DarkVenueHeatmapDataState.ts';
import State from 'store/State';
import { validateResponse } from 'widgets/darkVenueHeatmap/api/validation.ts';

const initialState: DarkVenueHeatmapDataState = {};

const darkVenueHeatmapDataSlice = createSlice({
    name: 'darkVenueHeatmapDataSlice',
    initialState,
    reducers: {
        addRequest: (
            state,
            action: PayloadAction<{
                request: DarkVenueHeatmapRequest;
                apiRequest: DarkVenueHeatmapApiRequest;
            }>
        ) => {
            const { request, apiRequest } = action.payload;
            const uid = toUid(request);
            state[uid] = {
                request,
                apiRequest,
                requestTimestamp: new Date().getTime(),
                status: 'In Progress',
            };
        },
        addResponse: (
            state,
            action: PayloadAction<{
                request: DarkVenueHeatmapRequest;
                response: DarkVenueHeatmapApiResponse;
            }>
        ) => {
            const {
                payload: { request, response },
            } = action;
            const uid = toUid(request);
            if (!state[uid]) {
                throw new Error('No request for uid ' + uid);
            }
            state[uid].response = response;
            state[uid].responseTimestamp = new Date().getTime();

            const responseValidationResult = validateResponse(response);
            if (responseValidationResult.isValid) {
                state[uid].status = 'Completed';
            } else {
                state[uid].status = 'Failed';
                state[uid].error = responseValidationResult.error;
            }
        },
        clearData: (
            state,
            action: PayloadAction<
                | {
                      request: DarkVenueHeatmapRequest;
                  }
                | string
            >
        ) => {
            if (typeof action.payload === 'string') {
                delete state[action.payload];
                return;
            }
            const {
                payload: { request },
            } = action;
            const uid = toUid(request);
            delete state[uid];
        },

        addError: (
            state,
            action: PayloadAction<{
                request: DarkVenueHeatmapRequest;
                error: unknown;
            }>
        ) => {
            const {
                payload: { error, request },
            } = action;
            const uid = toUid(request);
            state[uid].error = error;
            state[uid].status = 'Failed';
            state[uid].responseTimestamp = new Date().getTime();
        },
    },
});

export default darkVenueHeatmapDataSlice.reducer;

export const { addError, addRequest, addResponse, clearData } =
    darkVenueHeatmapDataSlice.actions;

export const selectDarkVenueHeatmapWidgetsData = (state: State) =>
    selectWidgetsDataState(state).darkVenueHeatmap;

export const selectDarkVenueHeatmapState = (state: State, uid: string) =>
    uid ? selectDarkVenueHeatmapWidgetsData(state)[uid] : undefined;
